import { useEffect, useMemo } from 'react';
import {
  Navigate, Route, Routes, useLocation, useNavigate, useNavigationType,
} from 'react-router-dom';
import { isPublicOnly } from '../utils/permissions';
import { useAuth } from '../context/AuthContext';
import { clearWorkspace } from '../utils/workspace';
import { adoptLegacyDraft, fieldStore } from '../utils/fieldBatches';
import { fieldOutbox } from '../utils/fieldOutbox';
import FieldCamera from '../components/field/FieldCamera';
import FieldGallery from '../components/field/FieldGallery';
import FieldBatchEditor from '../components/field/FieldBatchEditor';
import { FieldGeoProvider } from '../components/field/FieldGeoProvider';
import { EventLiveProvider, useEventLive } from '../components/events/EventLiveProvider';
import EventField from '../components/events/EventField';
import BriefingModal from '../components/events/BriefingModal';
import { useActiveMission } from '../utils/activeMission';
import api from '../utils/api';

/**
 * Field Mode — camera first.
 *
 * Assume one hand, bright sun, an unreliable connection, and someone who does
 * not want to read. Opening Field Mode shows the viewfinder at once; a
 * specimen is photographed without typing a word, every photo filed under
 * the view it shows and stamped with when and where it was taken; Save (the
 * "new specimen" button) hands the batch to the outbox and the camera is
 * ready for the next one. Everything with words in it waits for the gallery.
 *
 *   /field                    the camera (components/field/FieldCamera.js)
 *   /field/gallery            today's and earlier specimens, this phone's and
 *                             the server's (FieldGallery.js)
 *   /field/batch/:id          one batch of this phone's, to correct
 *   /field/collection/:id     one collection on the server
 *   /field/retro              a specimen found earlier, from photos already
 *                             on the phone (FieldBatchEditor.js)
 *   /field/event/:id          a live field event: map, board, chat, cards
 *                             (components/events/EventField.js, Part III)
 *
 * ONE GPS WATCH (FieldGeoProvider) while the camera or an event is on
 * screen, shared by both. Out on a live event (the active mission says so),
 * its heartbeat (EventLiveProvider) runs for every screen here, the camera
 * shows the team's mini-map, and an arrival the server decided opens the
 * waypoint's briefing wherever the person is. A `public` account reaches
 * Field Mode only as an event's guest: anywhere but an event's own screen
 * needs that event to be the active mission, else it goes back to /survey.
 *
 * They are routes, not tabs, so a phone's own Back goes from the gallery to
 * the camera rather than out of Field Mode. The screens' own back buttons go
 * back through the same history where they can (state fromCamera /
 * fromGallery), so a Back after a Save never returns to the saved form.
 *
 * The camera's Map chip and the gallery's map button open the national map
 * (/map, pages/NationalMap.js) with state fromField, pushed rather than
 * replaced: the phone's Back, or the map's "Back to Field Mode", returns to
 * the very screen, and the camera picks up its unfinished specimen again.
 *
 * LEAVING. Opened from the workspace chooser, the camera's Back clears the
 * workspace and goes back to the chooser. Opened from somewhere that said
 * where to come back to -- Home's isolates hub sends `state.returnTo: '/'` to
 * /field and /field/gallery -- Back goes there instead, workspace untouched.
 * That path is kept in sessionStorage (`mb_field_return`) for as long as
 * Field Mode is open, since its own screens navigate without it.
 *
 * WHERE A SPECIMEN LIVES UNTIL THE SERVER HAS IT.
 *
 * In this phone's own store (utils/fieldBatches.js), photos and all, from the
 * first snap: a closed tab, a flat battery or a week without signal loses
 * nothing. The outbox (utils/fieldOutbox.js) mints the specimen's code,
 * creates the collection, uploads the photos and submits it, saving each step
 * as it goes, and keeps going while its collector works elsewhere in the app.
 * Nothing here goes through the /sync queue: a photograph cannot wait there.
 *
 * `store` and `outbox` default to the page's own; tests pass theirs.
 */
export default function Field({ store: injectedStore, outbox: injectedOutbox }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const store = useMemo(() => injectedStore || fieldStore(), [injectedStore]);
  const outbox = useMemo(() => injectedOutbox || fieldOutbox(), [injectedOutbox]);
  const userId = user?.user_id ? String(user.user_id) : null;

  // Started, never stopped here: what is waiting goes on being sent after
  // the person leaves Field Mode.
  useEffect(() => { outbox.start(); }, [outbox]);

  // A capture the quick-capture screen left unfinished becomes a batch, so a
  // code it minted or a record it made is finished rather than forgotten. It
  // is not sent by itself: that draft had no owner, and on a shared phone the
  // person opening Field Mode now need not be the one who left it. It waits
  // in the gallery for them to look at it and send it (attention,
  // 'legacy_draft').
  useEffect(() => {
    if (!userId) return;
    adoptLegacyDraft(store, userId).catch(() => {});
  }, [store, userId]);

  // Where Back leaves for, decided once, on entering Field Mode: its own
  // screens navigate without state, and must not forget it. An entry that
  // names no return path forgets an old one -- unless it is Back/Forward into
  // Field Mode (the map's "Back to Field Mode", a reload), which comes back to
  // the same visit. Above the early return: the rules of hooks.
  useEffect(() => {
    rememberReturn(location.state?.returnTo, navigationType);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!userId) return null;

  const leave = () => {
    const back = takeReturn();
    if (back) {
      navigate(back, { replace: true });
      return;
    }
    clearWorkspace();
    // A public account has no workspace: its way back is the survey.
    navigate(isPublicOnly(user) ? '/survey' : '/workspace', { replace: true });
  };
  const shared = { store, outbox, userId, user };
  const routes = (
    <Routes>
      <Route index element={<FieldCamera {...shared} onLeave={leave} />} />
      <Route path="gallery" element={<FieldGallery {...shared} />} />
      <Route path="batch/:batchId" element={<FieldBatchEditor mode="local" {...shared} />} />
      <Route path="collection/:sampleId" element={<FieldBatchEditor mode="server" {...shared} />} />
      <Route path="retro" element={<FieldBatchEditor mode="retro" {...shared} />} />
      <Route path="event/:missionId" element={<EventField {...shared} />} />
      <Route path="*" element={<Navigate to="/field" replace />} />
    </Routes>
  );
  return <FieldScreens userId={userId} user={user} location={location}>{routes}</FieldScreens>;
}

/** The providers around Field Mode's screens: the one GPS watch, and a live
 *  event's heartbeat and arrival briefing while out on one. */
function FieldScreens({ userId, user, location, children }) {
  const mission = useActiveMission(userId);
  const eventId = mission?.event ? mission.missionId : null;
  const path = location.pathname.replace(/\/+$/, '');
  const onEvent = path.startsWith('/field/event/');
  // A public account comes here as an event's guest or, since public portal
  // 2a, to report sightings in Observation mode: no redirect any more.
  const watching = path === '/field' || onEvent || !!eventId;
  return (
    <FieldGeoProvider active={watching}>
      {eventId ? (
        <EventLiveProvider missionId={eventId}>
          {children}
          <ArrivalBriefing />
        </EventLiveProvider>
      ) : children}
    </FieldGeoProvider>
  );
}

/** The waypoint's briefing, the moment the server says the person arrived. */
function ArrivalBriefing() {
  const live = useEventLive();
  if (!live?.arrival) return null;
  const done = () => {
    const { waypoint_id: waypointId } = live.arrival;
    live.clearArrival();
    api.post(`/events/${live.missionId}/briefing-seen`, { waypoint_id: waypointId }).catch(() => {});
  };
  return <BriefingModal arrival={live.arrival} onDone={done} missionId={live.missionId} />;
}

const RETURN_KEY = 'mb_field_return';

// An in-app path only. sessionStorage throws in some privacy modes; Field Mode
// then simply leaves for the chooser, as it always did.
function rememberReturn(returnTo, navigationType) {
  try {
    if (typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      sessionStorage.setItem(RETURN_KEY, returnTo);
    } else if (navigationType !== 'POP') {
      sessionStorage.removeItem(RETURN_KEY);
    }
  } catch {
    /* nothing to remember with */
  }
}

function takeReturn() {
  try {
    const back = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return back;
  } catch {
    return null;
  }
}
